import { FC, useState } from "react";

import {
  Button,
  Flyout,
  FlyoutHeader,
  FlyoutTitle,
} from "@monolith-forensics/monolith-ui";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  EditIcon,
  TrashIcon,
} from "lucide-react";

import {
  DetailItem,
  DetailLabel,
  DetailValue,
  FlyoutContainer,
  HeaderContent,
} from "@/components/Flyouts";
import { usePermissions } from "@/hooks/usePermissions";
import { useLabAssetItem } from "../labAssets.query";
import { EditLabAssetModal } from "../Modals/EditLabAssetModal";
import { DeleteLabAssetModal } from "../Modals/DeleteLabAssetModal";
import { LabAsset } from "../labAssets.types";
import {
  LAB_ASSET_FIELDS_BY_TYPE,
  LabAssetColumnDefinitions,
} from "../Tables/LabAssetColumnDefinitions";
import { getLabAssetDisplayName } from "../labAssets.utils";
import { AssetTypeBadge } from "../components/AssetTypeBadge";

interface LabAssetFlyoutProps {
  show: boolean;
  onClose?: () => void;
  defaultInfo: LabAsset;
  onNext?: () => void;
  onPrevious?: () => void;
}

interface MainDetailsProps {
  defaultData: LabAsset;
}

const MainDetails: FC<MainDetailsProps> = ({ defaultData }) => {
  // Runtime data could carry an unexpected asset_type; fall back to showing
  // every field rather than crashing on an undefined set.
  const relevantFields: Set<string> | undefined =
    LAB_ASSET_FIELDS_BY_TYPE[defaultData.asset_type];

  return (
    <FlyoutContainer>
      <div className="event-details">
        {LabAssetColumnDefinitions.filter(
          (col) =>
            col.dataField !== "description" &&
            (!relevantFields || relevantFields.has(col.dataField))
        ).map((col) => {
          const key = col.dataField;
          const label = col.caption;
          // The asset_number renderer is the table's row-navigation cell,
          // whose null-number "View" placeholder is an affordance, not a
          // value — skip it here so a missing number renders blank. A real
          // number keeps the renderer's details-page link.
          const suppressRender =
            col.dataField === "asset_number" && !defaultData.asset_number;
          // The raw lookup is only safe for scalar columns; a column backed
          // by a joined object must supply `render`.
          const value =
            col.render && !suppressRender
              ? col.render({ rowData: defaultData })
              : (defaultData[key] as string | number | null);

          return (
            <DetailItem key={key}>
              <DetailLabel>{label}</DetailLabel>
              <DetailValue>{value}</DetailValue>
            </DetailItem>
          );
        })}
        <DetailItem>
          <DetailLabel>Description</DetailLabel>
        </DetailItem>
        <DetailValue>{defaultData.description || "None"}</DetailValue>
      </div>
    </FlyoutContainer>
  );
};

/**
 * Properties-only quick-view (ENG-1668). Related data (validations,
 * attachments, history) belongs to the details page (ENG-1382). Edit/Delete
 * here mirror EquipmentFlyout; the modals invalidate the item and list caches
 * themselves, so the table refreshes without a callback.
 */
export const LabAssetFlyout: FC<LabAssetFlyoutProps> = ({
  show,
  onClose,
  defaultInfo,
  onNext,
  onPrevious,
}) => {
  const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Shared with the details page: one queryFn and one 404 policy on the
  // item(uuid) cache key. Seed from the table row for an instant paint; the
  // epoch stamp marks the seed as already stale — inert under the app's
  // default staleTime of 0 (the refetch on open happens regardless), but it
  // keeps that refetch if a staleTime is ever configured.
  const { data } = useLabAssetItem(defaultInfo.uuid, {
    initialData: defaultInfo,
    initialDataUpdatedAt: 0,
  });

  // null is the hook's not-found signal: the asset was deleted since the
  // table row loaded — say so rather than presenting the stale snapshot as a
  // current record. Transient failures (network, 5xx) keep the snapshot: the
  // interceptor toast already surfaced them and the row data is still the
  // best available.
  const assetGone = data === null;

  // On a failed refetch react-query keeps serving the seed, but guard anyway
  // so an unexpected shape never reaches the field renderers.
  const itemData = data?.uuid ? data : defaultInfo;

  return (
    <Flyout open={show} onClose={() => onClose?.()}>
      <FlyoutHeader>
        <FlyoutTitle>
          {itemData.asset_number || getLabAssetDisplayName(itemData) || "Asset"}
        </FlyoutTitle>
        <HeaderContent>
          <div>Asset</div>
          <AssetTypeBadge type={itemData.asset_type} />
          {!assetGone && hasPermission(MONOLITH_PERMISSIONS.ASSET_UPDATE) && (
            <Button
              size="xxs"
              variant="text"
              leftSection={<EditIcon size={12} />}
              color="primary"
              onClick={() => setShowEditModal(true)}
            >
              Edit
            </Button>
          )}
          {!assetGone && hasPermission(MONOLITH_PERMISSIONS.ASSET_DELETE) && (
            <Button
              size="xxs"
              variant="text"
              leftSection={<TrashIcon size={12} />}
              color="error"
              onClick={() => setShowDeleteModal(true)}
            >
              Delete
            </Button>
          )}
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: 10,
              alignItems: "center",
              marginLeft: "auto",
            }}
          >
            <Button
              size="xxs"
              variant="outlined"
              onClick={() => onPrevious?.()}
              style={{ padding: "2px 4px" }}
            >
              <ChevronLeftIcon size={14} />
            </Button>
            <Button
              size="xxs"
              variant="outlined"
              onClick={() => onNext?.()}
              style={{ padding: "2px 4px" }}
            >
              <ChevronRightIcon size={14} />
            </Button>
          </div>
        </HeaderContent>
      </FlyoutHeader>
      {assetGone ? (
        <FlyoutContainer>
          <DetailValue>
            There was an error loading this asset. It may have been deleted
            since this table initially loaded.
          </DetailValue>
        </FlyoutContainer>
      ) : (
        <MainDetails defaultData={itemData} />
      )}
      {showEditModal && (
        <EditLabAssetModal
          show={showEditModal}
          onClose={() => setShowEditModal(false)}
          defaultInfo={itemData}
        />
      )}
      {showDeleteModal && (
        <DeleteLabAssetModal
          show={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          // The row is gone; the list cache was invalidated by the modal.
          onDelete={() => onClose?.()}
          defaultInfo={itemData}
        />
      )}
    </Flyout>
  );
};
